import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    asyncGetSurvey,
    asyncPostSurveyResponse,
} from '../../../services/survey.service';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FieldTextAnswer from '../../components/general/surveysField/fieldTextAnswer';
import FieldLinealAnswer from '../../components/general/surveysField/fieldLinealAnswer';
import FieldUniqueAnswer from '../../components/general/surveysField/fieldUniqueAnswer';
import FieldMultipleAnswers from '../../components/general/surveysField/fieldMultipleAnswers';
import { LoadingButton } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';
import FieldLinealLabelAnswer from '../../components/general/surveysField/fieldLinealLabelAnswer';

const SurveysFormView = () => {
    const { idSurvey } = useParams();
    const [survey, setSurvey] = useState<any>(undefined);
    const [questions, setQuestions] = useState<any[]>([]);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState<boolean>(false);
    const [save, setSave] = useState<boolean>(false);

    useEffect(() => {
        asyncGetSurvey(idSurvey, true)
            .then((body: any) => {
                if (body.items !== undefined) {
                    setSurvey(body?.items);
                }
            })
            .finally(() => setLoading(false));
    }, [idSurvey]);

    const handleChangeQuestion = (data: any) => {
        setAnswers({ ...answers, [data.idQuestion]: data });
    };

    const getResAnswers = () => {
        const resAnswers: any = {};
        questions.forEach((q: any) => {
            if ((answers as any)[q.id])
                resAnswers[q.id] = (answers as any)[q.id];
        });

        return resAnswers;
    };

    const isInvalid = (resAnswers?: any) => {
        if (!resAnswers) resAnswers = getResAnswers();
        return (
            Boolean(Object.values(resAnswers).find((a: any) => a.error)) ||
            questions.filter((q: any) => (answers as any)[q.id]).length !==
                questions.length
        );
    };

    const onClickSave = () => {
        const resAnswers = getResAnswers();
        if (isInvalid(resAnswers)) return;

        if (Object.values(resAnswers).length === questions.length) {
            setLoading(true);
            const data: any = {
                survey: idSurvey,
                response: resAnswers,
            };
            asyncPostSurveyResponse(data)
                .then((body: any) => {
                    if (body.items.ok) {
                        setSave(true);
                        setAnswers([]);
                    }
                })
                .finally(() => setLoading(false));
        }
    };

    useEffect(() => {
        setQuestions(
            survey?.questions?.filter(
                ({ questionDependency, position }: any) => {
                    const dep = questionDependency[0]?.questionDependency;
                    const depAnswer = questionDependency[0]?.answerDependency;
                    const depAnswerText =
                        questionDependency[0]?.answerTextDependency;

                    if (position > dep?.position) {
                        const currentAnswer = (answers as any)[dep?.id];
                        switch (dep?.type) {
                            case 'linear':
                                const range = depAnswerText.split('-');
                                try {
                                    return (
                                        parseInt(range[0]) <=
                                            parseInt(currentAnswer?.answer) &&
                                        parseInt(range[1]) >=
                                            parseInt(currentAnswer?.answer)
                                    );
                                } catch (error) {
                                    return false;
                                }
                            case 'multiple':
                                return (
                                    currentAnswer?.answer &&
                                    depAnswer?.id &&
                                    Boolean(
                                        currentAnswer?.answer?.find(
                                            (a: string) => a === depAnswer?.id
                                        )
                                    )
                                );
                            default:
                                return (
                                    currentAnswer?.answer &&
                                    depAnswer?.id &&
                                    currentAnswer?.answer === depAnswer?.id
                                );
                        }
                    }

                    return true;
                }
            ) || []
        );
    }, [survey, answers]);

    return (
        <Box>
            {!save ? (
                <>
                    <Typography
                        fontFamily="Lucida Sans"
                        fontSize="2rem"
                        fontWeight="700!important"
                        textAlign="center"
                        color="#4b4b4b"
                        key={idSurvey}
                    >
                        {survey?.title || ''}
                    </Typography>
                    <Grid container sx={{ textAlign: 'center', mt: 3 }}>
                        {survey &&
                            questions.map((q: any, index: number) => (
                                <Grid item xs={12} mt={2} key={q.id}>
                                    {q.type === 'multiple' ? (
                                        <FieldMultipleAnswers
                                            key={q.id}
                                            title={`${index + 1}. ${
                                                q?.description
                                            }`}
                                            answerQuestion={q?.answers || []}
                                            idQuestion={q.id}
                                            questionType={q.type}
                                            whyQuestion={q?.why}
                                            name="answer"
                                            onChange={handleChangeQuestion}
                                        />
                                    ) : q.type === 'only' ? (
                                        <FieldUniqueAnswer
                                            key={q.id}
                                            title={`${index + 1}. ${
                                                q?.description
                                            }`}
                                            answerQuestion={q?.answers || []}
                                            idQuestion={q.id}
                                            questionType={q.type}
                                            whyQuestion={q?.why}
                                            name="answer"
                                            onChange={handleChangeQuestion}
                                        />
                                    ) : q.type === 'linear' ? (
                                        <FieldLinealAnswer
                                            key={q.id}
                                            title={`${index + 1}. ${
                                                q?.description
                                            }`}
                                            answerQuestion={q?.answers[0] || []}
                                            idQuestion={q.id}
                                            questionType={q.type}
                                            questionIcon={q.icon}
                                            whyQuestion={q?.why}
                                            name="answer"
                                            onChange={handleChangeQuestion}
                                        />
                                    ) : q.type === 'linear-label' ? (
                                        <FieldLinealLabelAnswer
                                            key={q.id}
                                            title={`${index + 1}. ${
                                                q?.description
                                            }`}
                                            answerQuestion={q?.answers || []}
                                            idQuestion={q.id}
                                            questionType={q.type}
                                            questionIcon={q.icon}
                                            whyQuestion={q?.why}
                                            name="answer"
                                            onChange={handleChangeQuestion}
                                        />
                                    ) : (
                                        <FieldTextAnswer
                                            key={q.id}
                                            title={`${index + 1}. ${
                                                q?.description
                                            }`}
                                            idQuestion={q.id}
                                            questionType={q.type}
                                            required={true}
                                            placeholder="Dejanos aquí tus comentarios"
                                            onChange={handleChangeQuestion}
                                            type={q.type}
                                        />
                                    )}
                                </Grid>
                            ))}
                        <Grid item xs={12} mt={3}>
                            <LoadingButton
                                loading={loading}
                                disabled={isInvalid()}
                                type="submit"
                                onClick={onClickSave}
                                variant="contained"
                                endIcon={<SendIcon color="secondary" />}
                                sx={{
                                    height: '35px',
                                    borderRadius: '30px',
                                    backgroundColor: '#3C92FF',
                                    ':hover': {
                                        backgroundColor: '#0C53EC',
                                    },
                                }}
                            >
                                <Typography
                                    fontFamily="Lucida Sans"
                                    fontSize="14px"
                                    textTransform="capitalize"
                                >
                                    Enviar Respuestas
                                </Typography>
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </>
            ) : (
                <Typography
                    fontFamily="Lucida Sans"
                    fontSize="30px"
                    textAlign="center"
                    color="#4b4b4b"
                    key={idSurvey}
                    m={25}
                >
                    Encuesta guardada con éxito
                    <br />
                    <br />
                    ¡Gracias por tu colaboración!
                </Typography>
            )}
        </Box>
    );
};

export default SurveysFormView;
